<template>
  <div
    :class="[
      'code-pill',
      { 'code-pill--link': isClickable, 'code-pill--deletable': deletable, 'code-pill--no-color': !item.color },
    ]"
    :style="style"
    data-test-id="code-pill"
  >
    <component
      :is="component"
      :title="nameTooltip ? null : pillTitle"
      v-on="isClickable ? { click: handleClick } : {}"
      :type="isClickable ? 'button' : null"
      class="code-pill__link"
    >
      <span v-if="isIcon" class="code-pill__icon">
        <slot name="icon" />
      </span>

      <span v-else class="code-pill__grid">
        <ATooltip
          placement="top"
          :delay="{ show: 120, hide: 0 }"
          :disabled="!nameTooltip"
          class="code-pill__popover-trigger"
        >
          <div class="code-pill__name" data-test-id="code-pill-name">
            <template v-if="item.parent">
              <span class="code-pill__child-name">{{ item.name }}</span>
              <ChevronLeftIcon16 class="code-pill__name-separator" />
              <span class="code-pill__parent-name">{{ item.parent.name }}</span>
            </template>
            <span v-else class="code-pill__default-name">{{ pillName }}</span>
          </div>

          <template #popper>
            <div class="code-pill__popover" data-test-id="code-pill__popover">
              <div v-if="item.parent" class="code-pill__popover-name">
                <span class="code-pill__popover-child-name">{{ truncatedNames.code }}</span>
                <ChevronLeftIcon16 class="code-pill__popover-name-separator" />
                <span class="code-pill__popover-parent-name">{{ truncatedNames.parentCode }}</span>
              </div>
              <span v-else class="code-pill__popover-default-name">{{ truncatedNames.code }}</span>
            </div>
          </template>
        </ATooltip>

        <span v-if="avatarsEnabled && attachedAt && attachedBy" class="code-pill__user">
          <ATooltip placement="top" :delay="{ show: 50, hide: 0 }">
            <UserAvatar
              class="code-pill__user-avatar"
              size="small"
              :image="attachedBy.image"
              :firstname="attachedBy.firstname"
              :lastname="attachedBy.lastname"
            />

            <template #popper>
              <div data-test-id="code-pill__attached">
                <i18n-t keypath="codePill.attached" tag="span">
                  <template #name>
                    {{ userName }}
                  </template>

                  <template #timeAgo>
                    <TimeSince :date="new Date(attachedAt)" />
                  </template>
                </i18n-t>
              </div>
            </template>
          </ATooltip>
        </span>

        <span v-if="count" class="code-pill__count" data-test-id="code-pill__count">
          {{ count }}
        </span>
      </span>
    </component>

    <template v-if="deletable">
      <span v-if="loading" class="code-pill__loading-indicator">
        <span class="code-pill__delete-fade"></span>
        <LoadingIndicator />
      </span>
      <span v-else class="code-pill__delete">
        <AdaptiveColorButton
          :color="item.color || fallbackColors.delete"
          show-close
          data-test-id="code-pill-delete-button"
          @click.stop="$emit('delete')"
        />
      </span>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

import tinycolor from 'tinycolor2';
import AdaptiveColorButton from '@components/common/AdaptiveColorButton.vue';
import ChevronLeftIcon16 from '@components/Icons/ChevronLeftIcon16.vue';
import { colorGrey200, colorGrey500 } from '@sass/globals.module.scss';
import { ProjectCode } from '@/types';

const tooltipNamePartMaxLength = 1000;

const truncate = (name: string) => {
  let truncated = name;

  if (name.length > tooltipNamePartMaxLength) {
    truncated = `${name.slice(0, tooltipNamePartMaxLength)}…`;
  }

  return truncated;
};

export default defineComponent({
  emits: { delete: null },
  name: 'CodePill',

  components: {
    AdaptiveColorButton,
    ChevronLeftIcon16,
  },

  props: {
    item: {
      type: Object as PropType<ProjectCode>,
      required: true,
    },

    attachedAt: {
      type: String,
      required: false,
      default: null,
    },

    attachedBy: {
      type: Object,
      required: false,
      default: () => ({
        image: '',
        firstname: '',
        lastname: '',
      }),
    },

    count: {
      type: Number,
      default: null,
    },

    deletable: {
      type: Boolean,
      default: false,
    },

    /**
     * If true, uses styles / tags for interactivity
     * Note that "false" does not prevent from listering
     * on native click events in the parent component
     */
    clickable: {
      type: Boolean,
      default: false,
    },

    editable: {
      type: Boolean,
      default: false,
    },

    loading: {
      required: false,
      type: Boolean,
      default: false,
    },

    title: {
      type: String,
      required: false,
      default: '',
    },

    /**
     * Show name in tooltip.
     * Disables title.
     */
    nameTooltip: {
      type: Boolean,
      required: false,
      default: false,
    },

    type: {
      required: false,
      default: 'default',
      validator: (value: string) => ['default', 'icon'].includes(value),
    },

    avatarsEnabled: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      fallbackColors: {
        delete: colorGrey500,
      },
    };
  },

  computed: {
    isClickable(): boolean {
      return this.clickable || this.editable;
    },

    isIcon(): boolean {
      return this.type === 'icon';
    },

    component(): 'button' | 'span' {
      return this.isClickable ? 'button' : 'span';
    },

    style(): {
      '--background-color': string;
      '--background-color-hover': string;
    } {
      return {
        '--background-color': this.item.color,
        '--background-color-hover': this.isClickable ? this.colors.backgroundHover : this.item.color,
      };
    },

    pillName(): string {
      if (!this.item.name) {
        return '';
      }

      if (this.item.parent) {
        return `${this.item.name} < ${this.item.parent.name}`;
      }

      return this.item.fullname;
    },

    pillTitle(): string {
      if (this.title) {
        return this.title;
      }

      if (!this.item.name) {
        return null;
      }

      if (this.item.parent) {
        return `${this.truncatedNames.code} < ${this.truncatedNames.parentCode}`;
      }

      return this.truncatedNames.code;
    },

    truncatedNames(): { code: string; parentCode?: string } {
      const code = truncate(this.item.name);

      if (this.item.parent) {
        const parentCode = truncate(this.item.parent.name);

        return {
          code,
          parentCode,
        };
      }

      return {
        code,
      };
    },

    colors(): { backgroundHover: string } {
      return {
        backgroundHover: this.item.color ? tinycolor(this.item.color).darken(10).toHexString() : colorGrey200,
      };
    },

    userName(): string {
      if (!this.attachedBy) {
        return null;
      }

      if (this.attachedBy.isPlaceholder) return this.$t('placeholderUser', { name: this.attachedBy.firstname });

      return this.attachedBy.fullname || this.$t('deletedUser');
    },
  },

  methods: {
    handleClick() {
      if (this.editable) this.$store.dispatch('modal/show', { type: 'codeEdit', payload: { code: this.item } });
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';
@use 'mixins/typography';
.code-pill {
  position: relative;
  --current-background-color: var(--background-color);
  max-width: max-content; // Prevent the pill from becoming larger than its content

  // Give the user avatar padding to the right so that the delete icon doesn't obscure it when
  // hovering
  &.code-pill--deletable {
    .code-pill__user {
      padding-right: 10px;
    }
  }
}

.code-pill__link {
  display: block;
  border-radius: settings.$border-radius;
  white-space: nowrap;
  transition: all 0.1s ease-in-out;
  overflow: hidden;
  background: var(--current-background-color);

  @include typography.typography-14-normal(20px);
}

// show delete when hovering or focussing
.code-pill:hover,
.code-pill:focus-within {
  .code-pill__delete {
    opacity: 1;
  }
}

// hover state only if codepill is a button
.code-pill--link {
  .code-pill__link:focus {
    box-shadow: 0 0 0 3px var(--background-color-hover);
  }

  &:hover {
    --current-background-color: var(--background-color-hover);
  }
}

.code-pill--no-color {
  .code-pill__link {
    box-shadow: settings.$box-shadow-border-grey-50;

    &:focus {
      box-shadow: 0 0 0 3px colors.$color-blue-200;
    }
  }

  .code-pill__name,
  .code-pill__count {
    color: colors.$color-black;
  }
}

.code-pill__icon {
  position: relative;
  height: settings.$codePillHeight * 1px;
  display: flex;
  width: 28px;
  justify-content: center;
  align-items: center;
}

.code-pill__grid {
  position: relative;
  height: settings.$codePillHeight * 1px;
  display: grid;
  align-items: center;
  grid-gap: 8px;
  grid-auto-flow: column;
  padding: 0 10px;
}

.code-pill__name {
  color: colors.$color-white;
  @include typography.typography-baseline-alignment;
  height: 100%;
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.code-pill__default-name {
  display: block;
  @include typography.typography-ellipsis;
  align-items: center;
  line-height: 150%; // ensure descenders are not cut off
}

.code-pill__name-separator {
  display: inline-flex;
  align-items: center;
  padding-top: 1px;
  min-width: 16px;
}

.code-pill__child-name {
  @include typography.typography-ellipsis;
  display: block;
  min-width: 25px;
  flex-shrink: 1;
  line-height: 150%;
}

.code-pill__parent-name {
  @include typography.typography-ellipsis;
  display: block;
  min-width: 25px; // ensure it doesn't shrink to nothing if the child name is very long
  flex-shrink: 1.25;
  line-height: 150%;
}

.code-pill__count {
  display: flex;
  align-items: center;
  justify-content: center;
  color: colors.$color-white;
  font-weight: 500;
  font-size: 10px;
}

.code-pill__loading-indicator {
  position: absolute;
  top: 0;
  right: 8px;
  height: 100%;
  display: flex;
  align-items: center;
  transition: all 0.1s ease-in-out;
  color: colors.$color-white;
}

.code-pill__delete {
  position: absolute;
  top: 0;
  right: 8px;
  opacity: 0;
  height: 100%;
  display: flex;
  align-items: center;
  transition: all 0.1s ease-in-out;
}

.code-pill__delete-fade {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 200%;
  pointer-events: none;
  overflow: hidden;

  &::before {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: 50%;
    content: '';
    display: block;
    background: var(--current-background-color);
    box-shadow: 0 0 10px 10px var(--current-background-color);
    transition: all 0.1s ease-in-out;
  }
}

.code-pill__popover-trigger {
  min-width: 0; // constrain it to container width
}

.code-pill__popover {
  max-width: 480px;
}

.code-pill__popover-name {
  @include typography.typography-baseline-alignment;
}

.code-pill__popover-name-separator {
  display: inline-flex;
  align-items: center;
  padding-top: 3px;
  min-width: 16px;
}
</style>
